import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import { Link } from '../../../RouterCompatibility';
import CustomTooltip from '../../../Tooltip';
import config, { statuses } from '../../../../config';
import { useTemplateConfig } from '../../../../context/TemplateConfigContext';
import { getPath } from '../../../../utils/Router';
import { getAvailableDevices } from '../../../modules';
import DataComponentOffer from "../../../DataComponent/DataComponentOffer";

const GoalsPageList = ({ 
    goalEntries, 
    headerNames, 
    loading, 
    totalPages, 
    regions, 
    page, 
    onAction, 
    t 
}) => {
    const [state, setState] = useState({
        goalEntries: goalEntries || [],
        headerNames: headerNames || [],
        loading: loading || false,
        totalPages: totalPages || 0,
        regions: regions || {},
        page: page || config.initialPage
    });

    const { assets } = useTemplateConfig();
    const referralBadges = (t && t.referral) || {};

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            goalEntries: goalEntries || [],
            loading: loading,
            regions: regions || {},
            totalPages: totalPages || 0,
            page: page || config.initialPage
        }));
    }, [goalEntries, loading, regions, totalPages, page]);

    const onDataTableAction = useCallback((state, type) => {
        if (type !== 'firstRender') {
            if (onAction) {
                onAction({
                    page: state.page
                });
            }
        }
    }, [onAction]);

    const getAvailableRegions = useCallback((goal) => {
        let regions = (goal.goal.traffic_restrictions.regions || []).map((regionId) => {
            return state.regions[regionId];
        });
        return regions.join(', ');
    }, [state.regions]);

    const renderBadge = useCallback((goalEntry) => {
        if (goalEntry.offer.referral_offer && goalEntry.offer.advertiser.referral_advertiser) {
            return (
                <span className="badge badge-danger ml10" title={referralBadges.offer}>RO</span>
            );
        }
        return null;
    }, [referralBadges]);

    const getGoalEntriesList = useCallback(() => {
        const staticNames = t?.offersPage?.goalsList || {};
        const staticNamesOffer = t?.offersPage?.offer || {};

        return Object.values(
                (state.goalEntries || [])
                    .reduce((acc, elem) => {
                        const offerId = elem.offer.id;
                        if(!acc[offerId]) {
                            acc[offerId] = elem;
                        } else {
                            // multiple goals; aggregate
                            acc[offerId].goal.title = '(' + t.offersPage.goalsList.multiple + ')';
                            acc[offerId].goal.description = '';

                            if (
                                Array.isArray(acc[offerId].goal?.traffic_restrictions?.regions)
                                ||
                                Array.isArray(elem.goal?.traffic_restrictions?.regions)
                            ) {
                                acc[offerId].goal.traffic_restrictions.regions = [
                                    ...(acc[offerId].goal?.traffic_restrictions?.regions ?? []),
                                    ...(elem.goal?.traffic_restrictions?.regions ?? []),
                                ];
                            };
                            if (
                                Array.isArray(acc[offerId].goal?.traffic_restrictions?.devices)
                                ||
                                Array.isArray(elem.goal?.traffic_restrictions?.devices)
                            ) {
                                acc[offerId].goal.traffic_restrictions.devices = [
                                    ...(acc[offerId].goal?.traffic_restrictions?.devices ?? []),
                                    ...(elem.goal?.traffic_restrictions?.devices ?? []),
                                ];
                            };
                            if([acc[offerId].status, elem.status].includes('active')) {
                                // any goal active -> show offer as active
                                acc[offerId].status = 'active';
                            } else {
                                // all paused -> show offer as paused
                                acc[offerId].status = 'paused';
                            }
                        }
                        return acc;
                    }, {})
            )
            .map((goalEntry, i) => {
                let acessStatus = goalEntry.access_granted ? 'unlock' : 'lock';
                let acessLinkClass = goalEntry.access_granted ? 'green-lock' : 'grey-lock';
                const image = goalEntry.offer.image || assets.cap;
                const regions = getAvailableRegions(goalEntry);
                const devices = getAvailableDevices(goalEntry.goal.traffic_restrictions.devices);
                const offerPath = getPath('offer', {id: goalEntry.offer.id})+'?tab=promo';
                const offerPathInfo = getPath('offer', {id: goalEntry.offer.id})+'?tab=goals';

                let goalEntryDescription = goalEntry.goal.description.slice(0,56);
                if (goalEntryDescription.length < goalEntry.goal.description.length) {
                    goalEntryDescription += '...';
                }

                let goalEntryOfferTitle = goalEntry.offer.title.slice(0,48);
                if (goalEntryOfferTitle.length < goalEntry.offer.title.length) {
                    goalEntryOfferTitle += '...';
                }

                let goalEntryGoalTitle = goalEntry.goal.title.slice(0,38);
                if (goalEntryGoalTitle.length < goalEntry.goal.title.length) {
                    goalEntryGoalTitle += '...';
                }

                return {
                    accessIcon: (
                        <div className="text-center">
                            <Link
                                className="offers__table-img offer-image"
                                to={offerPathInfo}
                            >
                                <div className="offer-image__bar">
                                    {goalEntry.status === statuses.paused
                                        ? (
                                            <CustomTooltip id={`icon-paused-${i}`} tooltip={staticNamesOffer.paused}>
                                                <div className={`paused-icon`}>
                                                    <i className={`fa fa-pause-circle`} />
                                                </div>
                                            </CustomTooltip>
                                        )
                                        : null
                                    }
                                </div>
                                <img src={image} alt=""/>
                            </Link>
                        </div>
                    ),
                    title: (
                        <div className="title-wrapper">
                            <div className="offers__item-descr">
                                <Link
                                    className="offer-name bold"
                                    to={offerPathInfo}
                                >
                                    {goalEntry.status === statuses.paused ? '[ PAUSED ] ' : ''}
                                    {goalEntryOfferTitle}
                                    {renderBadge(goalEntry)}
                                </Link>
                                <div className="offers__item-description">
                                    <div className="offers__item-description-container">
                                        {goalEntryDescription}
                                    </div>
                                </div>
                                <div className="offers__item-goal"> <span>{staticNamesOffer.goal}:</span>
                                    <span className="goal-name">{goalEntryGoalTitle}</span></div>
                                <p className="offers__item-locales">
                                    {staticNamesOffer.geo}: {(goalEntry.goal.offer.locales || []).map( (locale,i) =>
                                        <span className={`fi fi-${locale}`} key={i}></span>
                                    )}
                                </p>
                                <div className="offers__item-creative">
                                    {Object.keys(goalEntry.creative_types).map((creative, i) =>
                                        goalEntry.creative_types[creative] > 0
                                        ? <div className='offers__item-creative-item' key={i}>
                                                {`${staticNamesOffer[creative]} (${goalEntry.creative_types[creative]})`}
                                        </div>
                                        : null
                                    )}
                                </div>
                            </div>
                            <div className="offers__item-promo-wrap">
                                <a href={offerPath} className="promo-button">Promo</a>
                                <a href={offerPathInfo}>
                                    <span className="info fa fa-info-circle fa-3x"></span>
                                </a>
                            </div>
                        </div>
                    )
                };
        });
    }, [state.goalEntries, getAvailableRegions, renderBadge, t]);

    return (
        <div className="offers__list">
            <DataComponentOffer
                className="offers__table"
                onAction={onDataTableAction}
                totalPages={state.totalPages}
                page={state.page}
                data={getGoalEntriesList()}
                loading={state.loading}
            />
        </div>
    );
};

GoalsPageList.propTypes = {
    goalEntries: PropTypes.array,
    headerNames: PropTypes.array,
    loading: PropTypes.bool,
    totalPages: PropTypes.number,
    regions: PropTypes.object,
    page: PropTypes.number,
    onAction: PropTypes.func,
    t: PropTypes.object
};

GoalsPageList.defaultProps = {
    goalEntries: [],
    headerNames: [],
    loading: false,
    totalPages: config.initialPage,
    regions: {},
    page: config.initialPage,
    onAction: () => {},
    t: {}
};

export default withTranslation(GoalsPageList);